import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from "@stencil/core";

import { StzhIframePreviewSkipEvent, StzhIframeView, StzhIframeViewChangeEvent } from "../../index";
import { StzhIframeLocalizedText } from "./stzh-iframe.localization";

import Cookies from "js-cookie";

// Save the original console.log method
const originalConsoleLog = console.log;

// Override console.log to suppress messages
console.log = function () {};

import iFrameResizeModule from "iframe-resizer";
// this import was only used in the resize function line 169, leaving here for the reviewer to decide if it can be deleted for good.
// import { getStzhEnvironment, StzhEnvironmentEnum } from "../../utils/utils";

console.log = originalConsoleLog;

const COOKIE_PREFIX_CONFIRM = "stzh-components-iframe-confirm";

let iframeCounter = 0;

/**
 * @slot preview-logo - Slot for logo
 * @slot preview-image - Slot for preview image
 * @slot cookiewall-text - Slot for cookiewall text
 */
@Component({
  tag: "stzh-iframe",
  styleUrl: "stzh-iframe.scss",
  scoped: true,
})
export class StzhIframe {
  /** Translation strings */
  @Prop() localization: StzhIframeLocalizedText;

  /** Id of iframe */
  @Prop() iframeId: string;

  /** Error text of iframe if couldn't be loaded */
  @Prop() iframeErrorText: string;

  /** Automatic height resizing of iFrame (Option SameSecondLevelDomain is Deprecated)*/
  @Prop() iframeResizerVariant: "None" | "PostMessage" | "SameSecondLevelDomain" = "None";

  /** Height correction for automatically resized iFrames */
  @Prop() iframeResizerHeightCorrection: number;

  /** Second level domain for automatically resized iFrames (Deprecated) */
  @Prop() iframeResizerSecondlevelDomain: string;

  /**
   * Name of iframe
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe?retiredLocale=de#name
   */
  @Prop() name: string;

  /**
   * Sandbox of iframe
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe?retiredLocale=de#sandbox
   */
  @Prop() sandbox: string;

  /**
   * ReferrerPolicy of Iframe
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe?retiredLocale=de#referrerpolicy
   */
  @Prop() referrerpolicy:
    | "no-referrer"
    | "no-referrer-when-downgrade"
    | "origin"
    | "origin-when-cross-origin"
    | "same-origin"
    | "strict-origin"
    | "strict-origin-when-cross-origin"
    | "unsafe-url";

  /**
   * Allow of Iframe
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe?retiredLocale=de#allow
   */
  @Prop() allow: string = "autoplay; fullscreen";

  /** Width of iframe */
  @Prop() width: string = "100%";

  /** Width of iframe */
  @Prop() height: string = "100%";

  /** `src` of iframe */
  @Prop() src: string = "";
  private _src: string | null = null;

  /**
   * Cookie name (if not empty, the user will be shown a cookie wall)
   * Will be prefixed with `stzh-components-iframe-confirm-`.
   */
  @Prop() cookie: string;

  /**
   * Number of days the cookie is valid for.
   * If omitted or set to null, the cookie expires when browser is closed.
   * Maximum is on year (365 days, is default).
   */
  @Prop() cookieExpires: number | null = 365;

  /** Confirm button icon */
  @Prop() cookieConfirmIcon: string;

  /** Confirm button label */
  @Prop() cookieConfirmLabel: string;

  /** Confirm text (use instead of `cookiewall-text` slot) */
  @Prop() cookieConfirmText: string;

  /** Preview label */
  @Prop() previewLabel: string;

  /** Preview icon */
  @Prop() previewIcon: string = "external-link";

  /** Whether preview should be skipped, if user has passed the cookiewall before */
  @Prop() skipPreviewIfCookieAccepted: boolean = false;

  /** Whether preview should always be skipped */
  @Prop() skipPreview: boolean = false;

  @Element() element: HTMLStzhIframeElement;

  @State() view: StzhIframeView = "preview";

  /** Preview skip (only gets executed if it goes directly to iframe and not cookiewall) */
  @Event() stzhPreviewSkip: EventEmitter<StzhIframePreviewSkipEvent>;

  /** View change */
  @Event() stzhViewChange: EventEmitter<StzhIframeViewChangeEvent>;

  @Watch("src")
  srcWatcher(newValue: string) {
    if (this.view === "iframe") {
      this._src = newValue;
    }
  }

  @Watch("view")
  viewWatcher(newValue: StzhIframeView) {
    if (newValue === "iframe") {
      this._src = this.src;
    } else {
      this._src = null;
    }
  }

  private internalId: string;
  private iframeElement: HTMLIFrameElement;
  private previewButtonElement: HTMLButtonElement;
  private confirmButtonElement: HTMLStzhButtonElement;
  private updateFocus: boolean = false;

  private handleIframeLoad = () => {
    if (this.updateFocus) {
      this.iframeElement.focus();
      this.updateFocus = false;
    }
    this.initializeIframeResizing();
  };

  private initializeIframeResizing() {
    switch (this.iframeResizerVariant) {
      case "None":
        return;
      case "PostMessage":
        iFrameResizeModule.iframeResize(
          {
            log: false, // getStzhEnvironment() !== StzhEnvironmentEnum.PROD,
            checkOrigin: false,
          },
          this.iframeElement,
        );
        break;
      case "SameSecondLevelDomain":
        console.warn(
          "stzh-iframe: sameSecondLevelDomain is deprecated as it does no longer work on evergreen browsers, use PostMessage instead.",
        );
        if (!this.iframeResizerSecondlevelDomain) {
          console.log("stzh-iframe:", "property 'iframeSecondlevelDomain' not filled out.");
        }
        this.setupIframeAutoSizing({
          frameId: this.iframeId,
          heightCorrection: this.iframeResizerHeightCorrection || 0,
          hasSameDomain: false,
          secondLevelDomain: this.iframeResizerSecondlevelDomain,
        });
        break;
    }
  }

  private setupIframeAutoSizing(config: {
    frameId: string;
    heightCorrection: number;
    hasSameDomain: boolean;
    secondLevelDomain: string;
  }) {
    const createResizer = (iframe: HTMLIFrameElement, config: { heightCorrection: number }) => {
      return () => {
        try {
          const newHeight = iframe.contentWindow?.document.body.scrollHeight || 0;
          if (newHeight > 0) {
            iframe.style.height = `${newHeight + config.heightCorrection}px`;
          }
        } catch (error) {
          console.warn("stzh-iframe:", "Resizing not possible, perhaps due to cross-domain restrictions:", error);
        }
      };
    };

    if (!config.hasSameDomain) {
      document.domain = config.secondLevelDomain;
    }

    const iframe = document.getElementById(config.frameId) as HTMLIFrameElement;
    if (!iframe) {
      console.error(`Iframe with ID ${config.frameId} not found`);
      return;
    }

    const resizer = createResizer(iframe, config);
    window.addEventListener("resize", resizer);
    iframe.addEventListener("load", resizer);
    iframe.addEventListener("blur", resizer);
    iframe.addEventListener("readystatechange", resizer); // hack for internet explorer...

    try {
      // this might go wrong on IE, if iframe hasn't fully loaded yet, but it doesn't matter.
      resizer();
    } catch (error) {
      console.warn("Initial resize failed, likely due to iframe not being fully loaded yet:", error);
    }
  }

  private handleCookiewallCloseClick = () => {
    this.updateFocus = true;
    this.view = "preview";

    this.stzhViewChange.emit({
      component: "stzh-iframe",
      value: this.view,
    });
  };

  private handleCookiewallConfirmClick = () => {
    Cookies.set(`${COOKIE_PREFIX_CONFIRM}-${this.cookie}`, "1", {
      expires: this.cookieExpires || null,
    });

    this.updateFocus = true;
    this.view = "iframe";

    this.stzhViewChange.emit({
      component: "stzh-iframe",
      value: this.view,
    });
  };

  private handlePreviewClick = () => {
    const cookieAccepted = Cookies.get(`${COOKIE_PREFIX_CONFIRM}-${this.cookie}`) || false;
    this.updateFocus = true;

    if (!this.cookie || cookieAccepted) {
      this.view = "iframe";
    } else {
      this.view = "cookiewall";
    }

    this.stzhViewChange.emit({
      component: "stzh-iframe",
      value: this.view,
    });
  };

  async componentWillLoad() {
    this.internalId = `stzh-iframe-${iframeCounter++}`;

    const cookieAccepted = Cookies.get(`${COOKIE_PREFIX_CONFIRM}-${this.cookie}`) || false;

    if (
      ((!this.cookie || cookieAccepted) && (this.skipPreviewIfCookieAccepted || this.skipPreview)) ||
      (!this.cookie && this.skipPreview)
    ) {
      this.stzhPreviewSkip.emit({
        component: "stzh-iframe",
      });

      this.view = "iframe";
    } else if (this.cookie && this.skipPreview) {
      this.view = "cookiewall";
    }

    this.viewWatcher(this.view);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "iframe");
    }
  }

  async componentDidRender() {
    if (this.updateFocus) {
      if (this.view === "iframe") {
        // wait for iframe load event (handleIframeLoad)
      } else if (this.view === "preview") {
        this.previewButtonElement.focus();
        this.updateFocus = false;
      } else if (this.view === "cookiewall") {
        this.confirmButtonElement.focus();
        this.updateFocus = false;
      }
    }
  }

  render() {
    const classes = {
      "stzh-iframe": true,
    };

    return (
      <Host>
        <div class={classes}>
          {this.view === "iframe" && (
            <iframe
              id={this.iframeId}
              name={this.name}
              sandbox={this.sandbox}
              referrerPolicy={this.referrerpolicy}
              width={this.width}
              height={this.height}
              ref={el => (this.iframeElement = el as HTMLIFrameElement)}
              class="stzh-iframe__iframe"
              src={this._src}
              allow={this.allow}
              onLoad={this.handleIframeLoad}
            >
              {this.iframeErrorText}
            </iframe>
          )}

          <button
            ref={el => (this.previewButtonElement = el as HTMLButtonElement)}
            onClick={this.handlePreviewClick}
            aria-label={this.previewLabel || this.localization.defaultPreviewLabel}
            // disabled={this.view === "cookiewall"}
            class={{
              "stzh-iframe__preview": true,
              "is-visible": this.view === "preview" || this.view === "cookiewall",
            }}
          >
            <div class="stzh-iframe__preview-image-wrapper">
              <slot name="preview-image"></slot>
            </div>

            <div class="stzh-iframe__preview-logo-wrapper">
              <slot name="preview-logo"></slot>
            </div>

            {this.previewIcon && (
              <div
                class={{
                  "stzh-iframe__preview-run": true,
                  "is-visible": this.view === "preview",
                }}
              >
                <stzh-icon class="stzh-iframe__preview-run-icon" name={this.previewIcon}></stzh-icon>
              </div>
            )}
          </button>

          {this.cookie && (
            <div
              class={{
                "stzh-iframe__cookiewall": true,
                "is-visible": this.view === "cookiewall",
              }}
            >
              <div class="stzh-iframe__cookiewall-content">
                <div class="stzh-iframe__cookiewall-text" id={`${this.internalId}-cookiewall-text`}>
                  {this.cookieConfirmText ? this.cookieConfirmText : <slot name="cookiewall-text"></slot>}
                </div>

                <stzh-button
                  ref={el => (this.confirmButtonElement = el as HTMLStzhButtonElement)}
                  class="stzh-iframe__cookiewall-button"
                  size="small"
                  a11yDescribedby={`${this.internalId}-cookiewall-text`}
                  icon={this.cookieConfirmIcon}
                  label={this.cookieConfirmLabel || this.localization.defaultConfirmLabel}
                  onClick={this.handleCookiewallConfirmClick}
                ></stzh-button>
              </div>

              {!this.skipPreview && (
                <button
                  class="stzh-iframe__cookiewall-close"
                  onClick={this.handleCookiewallCloseClick}
                  aria-label={this.localization.confirmCloseLabel}
                >
                  <stzh-icon class="stzh-iframe__cookiewall-close-icon" name="close"></stzh-icon>
                </button>
              )}
            </div>
          )}
        </div>
      </Host>
    );
  }
}
